import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import moment from 'moment';
import { CommonServices } from 'src/app/Common/CommonServices';
import { CommonService } from 'src/app/Services/common.service';
import {
  PlatformChargesClient,
  WebinarClient,
  WebinarRoomDTO,
  WebinarSettlementDetail
} from 'src/app/Services/WOWApi.Service';

@Component({
  selector: 'app-completed-webinar-settlement',
  templateUrl: './completed-webinar-settlement.component.html',
  styleUrls: ['./completed-webinar-settlement.component.css'],
  standalone: false
})
export class CompletedWebinarSettlementComponent implements OnInit {
  settlementForm: UntypedFormGroup;
  minDate: Date;
  roomsList: WebinarRoomDTO[] = [];
  settledWebinars: WebinarSettlementDetail[] | null | undefined;
  filteredSettlements: WebinarSettlementDetail[] = [];
  textSearch = '';
  isMobileView = false;
  hasSearched = false;
  subscription: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private webinarClient: WebinarClient,
    private platformChargesClient: PlatformChargesClient,
    private commonService: CommonService,
    private commonServices: CommonServices
  ) {}

  ngOnInit(): void {
    this.subscription = this.commonService.isMobileView.subscribe(res => this.isMobileView = res);
    this.buildForm();
    this.loadRooms();
  }

  buildForm(): void {
    const now = new Date();
    const startDate = moment(now).startOf('month').toDate();
    const endDate = moment(now).endOf('month').toDate();

    this.settlementForm = this.fb.group({
      StartDate: [startDate, [Validators.required]],
      EndDate: [endDate, [Validators.required]],
      RoomId: ['', [Validators.required]]
    });

    this.settlementForm.controls.StartDate.valueChanges.subscribe(value => {
      this.minDate = value;
    });
  }

  loadRooms(): void {
    this.webinarClient.getAllMAppedRoomsList().subscribe({
      next: (res) => {
        this.roomsList = res;
        const allRooms = new WebinarRoomDTO();
        allRooms.roomId = 0;
        allRooms.roomName = 'All Rooms';
        this.roomsList.unshift(allRooms);
        this.settlementForm.controls.RoomId.patchValue(this.roomsList[0].roomId.toString());
        this.searchSettlements();
      },
      error: (error) => {
        console.error(error);
        this.commonServices.showMessage('error', 'Failed to load rooms.', 4);
      }
    });
  }

  searchSettlements(): void {
    if (!this.settlementForm.valid) {
      this.commonServices.showMessage('error', 'Please provide start date, end date, and select a room.');
      return;
    }

    const roomId = this.settlementForm.controls.RoomId.value;
    const dateFrom = moment(this.settlementForm.controls.StartDate.value).format('yyy-MM-DD') + 'T00:00:00';
    const dateTo = moment(this.settlementForm.controls.EndDate.value).format('yyy-MM-DD') + 'T00:00:00';

    this.commonService.setSpinner(true);
    this.hasSearched = true;

    this.platformChargesClient.getRoomWiseSettlementBetweenDates(roomId, new Date(dateFrom), new Date(dateTo)).subscribe({
      next: (res) => {
        this.settledWebinars = res;
        this.applyTextFilter();
        this.commonService.setSpinner(false);
      },
      error: (error) => {
        this.commonService.setSpinner(false);
        this.commonServices.showMessage('error', error, 4);
      }
    });
  }

  onTextSearch(event: Event): void {
    this.textSearch = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.applyTextFilter();
  }

  applyTextFilter(): void {
    if (!this.settledWebinars || this.settledWebinars.length === 0) {
      this.filteredSettlements = [];
      return;
    }

    if (!this.textSearch) {
      this.filteredSettlements = [...this.settledWebinars].sort(
        (a, b) => new Date(b.settledOn).getTime() - new Date(a.settledOn).getTime()
      );
      return;
    }

    this.filteredSettlements = this.settledWebinars
      .filter(s =>
        (s.transactionNumber || '').toLowerCase().includes(this.textSearch) ||
        (s.webinar?.name || '').toLowerCase().includes(this.textSearch) ||
        (s.filePath || '').toLowerCase().includes(this.textSearch)
      )
      .sort((a, b) => new Date(b.settledOn).getTime() - new Date(a.settledOn).getTime());
  }

  getTotalSettledAmount(): number {
    return this.filteredSettlements.reduce((sum, s) => sum + (s.settledAmount || 0), 0);
  }

  getSelectedRoomName(): string {
    const roomId = Number(this.settlementForm?.controls?.RoomId?.value);
    return this.roomsList.find(x => x.roomId === roomId)?.roomName || 'selected room';
  }

  getDateRangeLabel(): string {
    const from = this.settlementForm?.controls?.StartDate?.value;
    const to = this.settlementForm?.controls?.EndDate?.value;
    if (!from || !to) {
      return '';
    }
    return `${moment(from).format('MMM D, YYYY')} – ${moment(to).format('MMM D, YYYY')}`;
  }

  hasResults(): boolean {
    return this.filteredSettlements.length > 0;
  }

  showEmptyState(): boolean {
    return this.hasSearched && this.settledWebinars !== undefined && !this.hasResults();
  }

  onOpenSettlementProof(base64Image: string): void {
    if (!base64Image) {
      this.commonServices.showMessage('error', 'No settlement proof available.', 4);
      return;
    }

    const image = 'data:image/png;base64,' + base64Image;
    const newWindow = window.open('', '_blank');

    if (newWindow) {
      newWindow.document.write(`
        <html>
          <head>
            <title>Settlement Proof</title>
            <style>
              body {
                margin: 0;
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100vh;
                background-color: #1f2937;
              }
              img {
                max-width: 100%;
                max-height: 100%;
                box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
              }
            </style>
          </head>
          <body>
            <img src="${image}" alt="Settlement proof" />
          </body>
        </html>
      `);
    } else {
      this.commonServices.showMessage('error', 'Popup blocked. Please allow popups to view the settlement proof.', 5);
    }
  }

  getProofFileName(filePath?: string): string {
    if (!filePath) {
      return 'View proof';
    }
    const parts = filePath.split(/[/\\]/);
    return parts[parts.length - 1] || 'View proof';
  }

  getShortProofFileName(filePath?: string): string {
    const fullName = this.getProofFileName(filePath);
    if (fullName.length <= 36) {
      return fullName;
    }
    const ext = fullName.includes('.') ? fullName.slice(fullName.lastIndexOf('.')) : '';
    const base = ext ? fullName.slice(0, fullName.lastIndexOf('.')) : fullName;
    if (base.length <= 28) {
      return fullName;
    }
    return `${base.slice(0, 14)}…${base.slice(-8)}${ext}`;
  }

  getGrossAmount(settlement: WebinarSettlementDetail): number {
    const webinar = settlement.webinar;
    if (webinar?.numberOfSeats != null && webinar?.pricePerSeat != null) {
      return Math.round(webinar.numberOfSeats * webinar.pricePerSeat * 100) / 100;
    }
    return Math.round(
      ((settlement.settledAmount || 0) + (settlement.platformCharges || 0) + this.getShippingCharges(settlement)) * 100
    ) / 100;
  }

  getShippingCharges(settlement: WebinarSettlementDetail): number {
    const webinar = settlement.webinar;
    if (webinar?.numberOfSeats != null && webinar?.pricePerSeat != null) {
      const gross = webinar.numberOfSeats * webinar.pricePerSeat;
      const shipping = gross - (settlement.platformCharges || 0) - (settlement.settledAmount || 0);
      return Math.max(0, Math.round(shipping * 100) / 100);
    }
    return 0;
  }
}
